import type { ProductConfig } from '@adminhub/product-config';

import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

export const enum ProductRequestsScreen {
  productRequestsScreen = 'productRequestsScreen',
  approveProductRequestAddingProductScreen = 'approveProductRequestAddingProductScreen',
  approveProductRequestBeforeApprovedScreen = 'approveProductRequestBeforeApprovedScreen',
  approveProductRequestAddDetailsScreen = 'approveProductRequestAddDetailsScreen',
}

type ScreenEventKeys =
  | 'approveProductRequestBeforeApprovedScreenEvent'
  | 'approveProductRequestAddDetailsScreen'
  | 'approveProductRequestAddingProductScreen';

type TrackEventKeys = 'productCreatedEvent' | 'productStatusUpdated';

type UiKeys =
  | 'approveProductRequestButtonClicked'
  | 'drawerCloseButtonClicked'
  | 'learnAboutProductRequestsLinkClicked'
  | 'closeButtonClicked'
  | 'nextButtonClicked'
  | 'confirmButtonClicked'
  | 'inviteUsersButtonClicked'
  | 'inviteUsersLinkClicked'
  | 'backButtonClicked';

export const approveProductEvents: FeatureEvents<ScreenEventKeys, UiKeys, TrackEventKeys, string> = {
  confluenceSpec:
    'https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/3998848562/Shadow+IT+M3+-+Approve+Add+Product+Request+Analytics+Spec',
  screen: {
    approveProductRequestBeforeApprovedScreenEvent: (attributes: { productName: ProductConfig['productName'] }): ScreenEvent => ({
      data: {
        name: ProductRequestsScreen.approveProductRequestBeforeApprovedScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69934',
    }),
    approveProductRequestAddDetailsScreen: (attributes: { productName: ProductConfig['productName'] }): ScreenEvent => ({
      data: {
        name: ProductRequestsScreen.approveProductRequestAddDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69935',
    }),
    approveProductRequestAddingProductScreen: (attributes: { productName: ProductConfig['productName'] }): ScreenEvent => ({
      data: {
        name: ProductRequestsScreen.approveProductRequestAddingProductScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69936',
    }),
  },
  ui: {
    approveProductRequestButtonClicked: (attributes: { productName: ProductConfig['productName'] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'approveProductRequest',
        source: ProductRequestsScreen.productRequestsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69937',
    }),
    inviteUsersButtonClicked: (attributes: { productName: ProductConfig['productName'] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'inviteUsers',
        source: ProductRequestsScreen.approveProductRequestAddingProductScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14416',
    }),
    confirmButtonClicked: (attributes: { productName: ProductConfig['productName'] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirm',
        source: ProductRequestsScreen.approveProductRequestAddDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/25543',
    }),
    nextButtonClicked: (attributes: { productName: ProductConfig['productName'] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'next',
        source: ProductRequestsScreen.approveProductRequestBeforeApprovedScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/25541',
    }),
    backButtonClicked: (attributes: { productName: ProductConfig['productName'] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'back',
        source: ProductRequestsScreen.approveProductRequestBeforeApprovedScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/28051',
    }),
    drawerCloseButtonClicked: (
      attributes: { productName: ProductConfig['productName'] },
      {
        source,
      }: {
        source:
          | ProductRequestsScreen.approveProductRequestBeforeApprovedScreen
          | ProductRequestsScreen.approveProductRequestAddDetailsScreen
          | ProductRequestsScreen.approveProductRequestAddingProductScreen;
      },
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'drawerClose',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/47441',
    }),
    closeButtonClicked: (attributes: { productName: ProductConfig['productName'] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'close',
        source: ProductRequestsScreen.approveProductRequestAddingProductScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14881',
    }),
    learnAboutProductRequestsLinkClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'learnAboutProductRequests',
        source: ProductRequestsScreen.approveProductRequestBeforeApprovedScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69938',
    }),
    inviteUsersLinkClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'inviteUsers',
        source: ProductRequestsScreen.approveProductRequestAddingProductScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69939',
    }),
  },
  track: {
    productCreatedEvent: (attributes: { productName: ProductConfig['productName'] }): TrackEvent => ({
      data: {
        action: TrackAction.Updated,
        actionSubject: TrackActionSubject.Instance,
        source: ProductRequestsScreen.productRequestsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/34968',
    }),
    productStatusUpdated: (attributes: { productName: ProductConfig['productName']; success: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Updated,
        actionSubject: TrackActionSubject.ProductStatus,
        source: ProductRequestsScreen.productRequestsScreen,
        attributes: {
          ...attributes,
          newStatus: 'APPROVED',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/52984',
    }),
  },
  operational: {},
};
